/**
 * Alert email links (Eric, 2026-10-28) arrive as /alert-action#t=<token>. The app
 * shell calls captureAlertActionFragment() first, in instrument.js, before Sentry or
 * PostHog start. That way the token is never in a breadcrumb or a session property
 * (PR #1064 review). It keeps the token for this tab only (sessionStorage, plus
 * memory if storage is blocked) so it survives the sign-in hop, and drops the
 * fragment from the address bar. A fragment is never sent to a server, so the
 * token isn't in any request log either. Same pattern as the P7 handoff page
 * (src/handoff/captureFragment.ts).
 */
export const ALERT_ACTION_TOKEN_KEY = 'ht:alert-action-token';

const PAGE = /^\/alert-action\/?$/;

let inMemory: string | null = null;

type Loc = Pick<Location, 'hash' | 'pathname' | 'search'>;
type Hist = Pick<History, 'replaceState' | 'state'>;

export function captureAlertActionFragment(loc: Loc = window.location, hist: Hist = window.history): void {
  if (!PAGE.test(loc.pathname) || !loc.hash) return;
  try {
    // A new link replaces any earlier one in this tab, even when the new one is
    // malformed: the page must never act on a stale link (Codex, #1064).
    forgetAlertActionToken();
    const token = new URLSearchParams(loc.hash.slice(1)).get('t');
    if (token && token.length <= 600) {
      inMemory = token;
      try {
        sessionStorage.setItem(ALERT_ACTION_TOKEN_KEY, token);
      } catch {
        // storage blocked: the in-memory copy still serves this page view
      }
    }
  } finally {
    // Always drop the fragment on this page, even a malformed one.
    hist.replaceState(hist.state ?? null, '', loc.pathname + loc.search);
  }
}

/** The link token for this tab, if one was captured. */
export function readAlertActionToken(): string | null {
  try {
    return sessionStorage.getItem(ALERT_ACTION_TOKEN_KEY) ?? inMemory;
  } catch {
    return inMemory;
  }
}

/** Done with the link: forget it in this tab. */
export function forgetAlertActionToken(): void {
  inMemory = null;
  try {
    sessionStorage.removeItem(ALERT_ACTION_TOKEN_KEY);
  } catch {
    // storage blocked: nothing was kept there
  }
}
